import { createSignal, createResource, createMemo, createEffect, For, Show } from 'solid-js';
import { formatMoney as fmt } from '../lib/money.js';
import { runStatusLabel } from '../lib/labels.js';

// Slip Gaji — printable per-employee slip from a run's stored results.
// Visibility rules (skema default + pengecualian karyawan) only filter the
// printed LINES; totals are always the real computed values (owner decision).

async function getJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('HTTP ' + res.status);
  return res.json();
}

export default function Slip() {
  const [schemes] = createResource(() => getJSON('/api/schemes'));
  const [scheme, setScheme] = createSignal('');
  createEffect(() => {
    const list = schemes();
    if (list?.length && !scheme()) setScheme(list[0].code);
  });

  const [runs] = createResource(
    scheme,
    (code) => code ? getJSON(`/api/runs?scheme=${code}`) : null,
  );
  const [runId, setRunId] = createSignal(0);
  createEffect(() => {
    const list = runs();
    if (list?.length) setRunId(list[0].id);
    else setRunId(0);
  });

  const [slips, { refetch: refetchSlips }] = createResource(
    runId,
    (id) => id ? getJSON(`/api/run/${id}/slips`) : null,
  );
  const [vis, { refetch: refetchVis }] = createResource(
    scheme,
    (code) => code ? getJSON(`/api/scheme/${code}/slipvis`) : null,
  );

  const [nik, setNik] = createSignal('');
  createEffect(() => {
    const s = slips();
    if (s?.slips?.length && !s.slips.some(x => x.nik === nik())) setNik(s.slips[0].nik);
  });
  const slip = createMemo(() => slips()?.slips?.find(s => s.nik === nik()));

  // Slip title = "SLIP " + the run's Tipe Penggajian label (e.g. "SLIP KOMISI"),
  // falling back to "SLIP GAJI" when the run has no tipe.
  const slipTitle = () => {
    const t = slips()?.run?.tipeLabel;
    return t ? `SLIP ${t.toUpperCase()}` : 'SLIP GAJI';
  };

  // Filter the slip list by NIK or name.
  const [slipQ, setSlipQ] = createSignal('');
  const filteredSlips = createMemo(() => {
    const q = slipQ().trim().toLowerCase();
    const list = slips()?.slips ?? [];
    return q ? list.filter(s => `${s.nik} ${s.name}`.toLowerCase().includes(q)) : list;
  });

  const [editVis, setEditVis] = createSignal(false);
  const [showSchemePanel, setShowSchemePanel] = createSignal(false);

  // Signature-block editor (per-scheme Direksi/HRD/city). Seeded from the run's
  // resolved sign on open; saved back to the scheme, then slips refetched so the
  // preview updates.
  const [editSign, setEditSign] = createSignal(false);
  const [signForm, setSignForm] = createSignal({});
  function openSign() {
    setSignForm({ ...(slips()?.sign ?? {}) });
    setEditVis(false);
    setEditSign(true);
  }
  function signField(k, v) { setSignForm({ ...signForm(), [k]: v }); }
  async function saveSign() {
    await fetch(`/api/scheme/${scheme()}/signatories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(signForm()),
    });
    setEditSign(false);
    refetchSlips();
  }

  // Batch print: render EVERY slip stacked (page-break per slip), print,
  // then return to the single-slip view.
  const [batch, setBatch] = createSignal(false);
  function printAll() {
    setBatch(true);
    setTimeout(() => {
      window.print();
      setBatch(false);
    }, 150);
  }

  // Effective rule + source for one component of the selected employee.
  function ruleFor(code) {
    const v = vis();
    if (!v) return { state: 'show', source: 'default' };
    const emp = v.employees?.[nik()];
    if (emp && code in emp) return { state: emp[code] ? 'hide' : 'show', source: 'karyawan' };
    if (v.scheme?.[code]) return { state: 'hide', source: 'skema' };
    return { state: 'show', source: 'default' };
  }

  async function setRule(targetNik, code, state) {
    await fetch(`/api/scheme/${scheme()}/slipvis`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nik: targetNik, component: code, state }),
    });
    refetchVis();
    refetchSlips();
  }

  // Save (or clear, when blank) the slip display text for a component.
  async function saveLabel(code, label) {
    await fetch(`/api/scheme/${scheme()}/sliplabel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ component: code, label }),
    });
    refetchVis();
    refetchSlips();
  }

  // Cycle the per-employee tri-state: inherit → hide → show → inherit.
  function cycleEmp(code) {
    const v = vis();
    const emp = v?.employees?.[nik()];
    const cur = emp && code in emp ? (emp[code] ? 'hide' : 'show') : 'inherit';
    const next = cur === 'inherit' ? 'hide' : cur === 'hide' ? 'show' : 'inherit';
    setRule(nik(), code, next);
  }

  return (
    <>
      <div class="page-head sl-controls">
        <h2>Slip Gaji</h2>
        <div class="spacer" />
        <Show when={schemes()}>
          <div class="seg">
            <For each={schemes()}>{(s) =>
              <button class="seg-item" classList={{ active: scheme() === s.code }}
                onClick={() => setScheme(s.code)}>{s.code}</button>
            }</For>
          </div>
        </Show>
        <select class="kg-select" value={runId()} onInput={(e) => setRunId(Number(e.currentTarget.value))}>
          <For each={runs() ?? []}>{(r) =>
            <option value={r.id}>{r.periodStart}{r.tipeLabel ? ` — ${r.tipeLabel}` : ''} — {runStatusLabel(r.status)}{r.legacyNo ? ` (${r.legacyNo})` : ''}</option>
          }</For>
        </select>
      </div>

      <div class="sl-layout">
        {/* ── Employee list ───────────────────────────────────────────── */}
        <div class="card sl-list sl-controls">
          <div class="sl-search">
            <input type="search" class="ky-search" placeholder="Cari NIK atau nama…"
              value={slipQ()} onInput={(e) => setSlipQ(e.currentTarget.value)} />
          </div>
          <For each={filteredSlips()}>{(s) =>
            <button class="kg-outline-item" classList={{ 'sl-active': s.nik === nik() }}
              onClick={() => setNik(s.nik)}>
              <span class="kg-outline-code">{s.nik} · {s.name}</span>
              <span class="kg-outline-mode">{fmt(s.bersih)}</span>
            </button>
          }</For>
          <Show when={(slips()?.slips?.length ?? 0) > 0 && filteredSlips().length === 0}>
            <div class="sl-list-empty kg-meta-text">Tidak ada karyawan yang cocok.</div>
          </Show>
        </div>

        {/* ── Slip preview ───────────────────────────────────────────── */}
        <div class="sl-main">
          <div class="sl-actions sl-controls">
            <button class="btn" onClick={() => { setEditVis(!editVis()); setShowSchemePanel(false); setEditSign(false); }}>
              {editVis() ? 'Selesai Atur Tampilan' : 'Atur Tampilan'}
            </button>
            <button class="btn" onClick={() => editSign() ? setEditSign(false) : openSign()}>
              {editSign() ? 'Tutup Tanda Tangan' : 'Tanda Tangan'}
            </button>
            <Show when={editVis()}>
              <button class="btn ghost" onClick={() => setShowSchemePanel(!showSchemePanel())}>
                aturan skema ({Object.keys(vis()?.scheme ?? {}).length})
              </button>
            </Show>
            <div class="spacer" />
            <button class="btn" onClick={printAll} title="Cetak semua slip — satu halaman per karyawan">
              Cetak Semua ({slips()?.slips?.length ?? 0})
            </button>
            <button class="btn primary" onClick={() => window.print()}>Cetak</button>
          </div>

          {/* scheme-wide rules panel: hide toggle + display-text per component */}
          <Show when={editVis() && showSchemePanel()}>
            <div class="card card-pad sl-scheme-panel sl-controls">
              <p class="ky-vars-note">
                Atur teks tampil & sembunyikan baris untuk <b>semua karyawan {scheme()}</b>
                {' '}(kecuali pengecualian per karyawan). Teks kosong = pakai nama bawaan;
                total slip selalu nilai sebenarnya.
              </p>
              <table class="data sl-label-table">
                <thead>
                  <tr><th class="sl-hide-col">Sembunyikan</th><th>Komponen (kode)</th><th>Teks di slip</th></tr>
                </thead>
                <tbody>
                  <For each={slips()?.components ?? []}>{(c) =>
                    <tr>
                      <td class="sl-hide-col">
                        <input type="checkbox" checked={!!vis()?.scheme?.[c.code]}
                          onInput={(e) => setRule('', c.code, e.currentTarget.checked ? 'hide' : 'show')} />
                      </td>
                      <td class="kg-code">{c.code}</td>
                      <td>
                        <Show when={c.hasKet} fallback={
                          <input class="sl-label-input" type="text" placeholder={c.label}
                            value={vis()?.labels?.[c.code] ?? ''}
                            onChange={(e) => saveLabel(c.code, e.currentTarget.value)} />
                        }>
                          <span class="kg-meta-text">Keterangan diatur otomatis di Komponen Gaji (klausa <code>ket</code>)</span>
                        </Show>
                      </td>
                    </tr>
                  }</For>
                </tbody>
              </table>
            </div>
          </Show>

          {/* signature-block settings (per scheme) */}
          <Show when={editSign()}>
            <div class="card card-pad sl-scheme-panel sl-controls">
              <p class="ky-vars-note">
                Tanda tangan slip untuk <b>semua karyawan {scheme()}</b>. Nama Penerima
                mengikuti karyawan pada tiap slip; kosongkan kota untuk memakai kota perusahaan.
              </p>
              <div class="sl-sign-form">
                <label>Kota tanda tangan
                  <input value={signForm().city ?? ''} placeholder="(kota perusahaan)"
                    onInput={(e) => signField('city', e.currentTarget.value)} />
                </label>
                <label>Label di bawah nama Penerima
                  <input value={signForm().penerimaLabel ?? ''} placeholder="(jabatan karyawan)"
                    onInput={(e) => signField('penerimaLabel', e.currentTarget.value)} />
                </label>
                <label>Nama Direksi (Menyetujui)
                  <input value={signForm().direksiName ?? ''} placeholder="cth. NUR KHOLIS"
                    onInput={(e) => signField('direksiName', e.currentTarget.value)} />
                </label>
                <label>Jabatan Direksi
                  <input value={signForm().direksiTitle ?? ''} placeholder="Direktur Utama"
                    onInput={(e) => signField('direksiTitle', e.currentTarget.value)} />
                </label>
                <label>Nama HRD (Yang Membuat)
                  <input value={signForm().hrdName ?? ''} placeholder="cth. YULIANA AROFAH"
                    onInput={(e) => signField('hrdName', e.currentTarget.value)} />
                </label>
                <label>Jabatan HRD
                  <input value={signForm().hrdTitle ?? ''} placeholder="HRD"
                    onInput={(e) => signField('hrdTitle', e.currentTarget.value)} />
                </label>
              </div>
              <button class="btn primary" onClick={saveSign}>Simpan Tanda Tangan</button>
            </div>
          </Show>

          <Show when={!batch() && slip()}>
            <SlipCard
              run={slips().run} slip={slip()} meta={slips()} title={slipTitle()}
              editVis={editVis()} ruleFor={ruleFor} cycleEmp={cycleEmp}
              components={slips().components}
            />
          </Show>

          {/* batch mode: every slip stacked, one page each */}
          <Show when={batch()}>
            <div class="sl-batch">
              <For each={slips().slips}>{(s) =>
                <SlipCard run={slips().run} slip={s} meta={slips()} title={slipTitle()} />
              }</For>
            </div>
          </Show>
        </div>
      </div>
    </>
  );
}

// SlipCard: one employee's slip. Edit-mode props (editVis/ruleFor/cycleEmp)
// are optional — batch printing renders the same card without controls.
function SlipCard(props) {
  const edit = () => !!props.editVis;
  const rule = (code) => props.ruleFor ? props.ruleFor(code) : { state: 'show', source: 'default' };
  const cycle = (code) => props.cycleEmp?.(code);
  return (
    <div class="card slip">
      <div class="slip-head">
        <div class="slip-head-id">
          <Show when={props.meta?.hasLogo}>
            <img class="slip-logo" src={`/api/company/${props.meta.companyId}/logo`} alt="" />
          </Show>
          <div>
            <div class="slip-brand">{props.meta?.company ?? 'Metalindo Payroll System'}</div>
            <Show when={props.meta?.address}><div class="slip-addr">{props.meta.address}</div></Show>
            <div class="slip-title">{props.title || 'SLIP GAJI'}</div>
          </div>
        </div>
        <div class="slip-period">
          Periode {props.run.periodStart} — {props.run.periodEnd}
          <Show when={props.run.legacyNo}><br />No. {props.run.legacyNo}</Show>
        </div>
      </div>
      <div class="slip-emp">
        <b>{props.slip.name}</b> · {props.slip.nik}
        <Show when={props.slip.position}> · {props.slip.position}</Show>
        <Show when={props.slip.department}> · {props.slip.department}</Show>
      </div>

      <div class="slip-cols">
        <div>
          <div class="slip-sec">PENDAPATAN</div>
          <For each={props.slip.earnings}>{(l) =>
            <div class="slip-line" classList={{ 'slip-skip': l.skipTotal }}>
              <span>{l.label}<VisBtn show={edit()} rule={rule(l.code)} onClick={() => cycle(l.code)} /></span>
              <span class="money">{fmt(l.amount)}{l.skipTotal ? ' *' : ''}</span>
            </div>
          }</For>
        </div>
        <div>
          <div class="slip-sec">POTONGAN</div>
          <For each={props.slip.deductions}>{(l) =>
            <div class="slip-line" classList={{ 'slip-skip': l.skipTotal }}>
              <span>{l.label}<VisBtn show={edit()} rule={rule(l.code)} onClick={() => cycle(l.code)} /></span>
              <span class="money">{fmt(l.amount)}{l.skipTotal ? ' *' : ''}</span>
            </div>
          }</For>
        </div>
      </div>

      {/* hidden lines exist only as an ADMIN hint, never printed */}
      <Show when={edit() && props.slip.hiddenCount > 0}>
        <div class="sl-hidden-hint sl-controls">
          {props.slip.hiddenCount} baris bernilai disembunyikan — total di bawah tetap nilai sebenarnya.
          <HiddenList slip={props.slip} components={props.components} ruleFor={props.ruleFor} cycle={props.cycleEmp} />
        </div>
      </Show>

      <div class="slip-totals">
        <div class="slip-line"><span>Total Pendapatan</span><span class="money">{fmt(props.slip.bruto)}</span></div>
        <div class="slip-line"><span>Total Potongan</span><span class="money">{fmt(props.slip.potongan)}</span></div>
        <div class="slip-line slip-net"><span>DITERIMA BERSIH</span><span class="money">{fmt(props.slip.bersih)}</span></div>
        <Show when={props.slip.earnings.some((l) => l.skipTotal) || props.slip.deductions.some((l) => l.skipTotal)}>
          <div class="slip-skip-note">* tidak termasuk dalam total</div>
        </Show>
      </div>

      <Show when={props.slip.bankName}>
        <div class="slip-bank">Transfer: {props.slip.bankName} · {props.slip.bankAccount}</div>
      </Show>
      <Show when={props.slip.notes?.length > 0}>
        <div class="slip-notes">
          <For each={props.slip.notes}>{(n) =>
            <div class="slip-note">
              {n.text}<VisBtn show={edit()} rule={rule(n.code)} onClick={() => cycle(n.code)} />
            </div>
          }</For>
        </div>
      </Show>
      <Show when={props.meta?.footer}>
        <div class="slip-footer">{props.meta.footer}</div>
      </Show>

      <SignBlock sign={props.meta?.sign} slip={props.slip} />
    </div>
  );
}

// Tanggal cetak in Indonesian long form, e.g. "14 Juni 2026".
function printDate() {
  return new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
}

// SignBlock: the tanda-tangan footer — "Mengetahui {kota}, {tanggal}" over three
// columns. Penerima = the slip's own employee; Menyetujui/Yang Membuat come from
// the per-scheme signatory settings. Always rendered (blank names → blank lines
// to hand-sign). Print-safe: pure content, no controls.
function SignBlock(props) {
  const sign = () => props.sign ?? {};
  // Employee column: a per-scheme fixed label wins; otherwise the slip's jabatan.
  const penTitle = () => sign().penerimaLabel || props.slip.position || '';
  const penSub = () => sign().penerimaLabel ? '' : (props.slip.department || '');
  return (
    <div class="slip-sign">
      <div class="slip-sign-date">
        {sign().city ? sign().city + ', ' : ''}{printDate()}
      </div>
      <div class="slip-sign-cols">
        <SignCol role="Penerima," name={props.slip.name} title={penTitle()} sub={penSub()} />
        <SignCol role="Menyetujui," name={sign().direksiName} title={sign().direksiTitle} />
        <SignCol role="Yang Membuat," name={sign().hrdName} title={sign().hrdTitle} />
      </div>
    </div>
  );
}

function SignCol(props) {
  return (
    <div class="slip-sign-col">
      <div class="slip-sign-role">{props.role}</div>
      <div class="slip-sign-space" />
      <div class="slip-sign-name">{props.name || ' '}</div>
      <Show when={props.title}><div class="slip-sign-title">{props.title}</div></Show>
      <Show when={props.sub}><div class="slip-sign-title">{props.sub}</div></Show>
    </div>
  );
}

// VisBtn: per-line tri-state toggle (edit mode only). Click cycles
// inherit → hide → show(forced) → inherit. Badge shows the rule source.
function VisBtn(props) {
  return (
    <Show when={props.show}>
      <button
        class="sl-vis sl-controls"
        classList={{ hide: props.rule.state === 'hide' }}
        title={`klik untuk mengubah — aturan saat ini: ${props.rule.state} (${props.rule.source})`}
        onClick={props.onClick}
      >
        {props.rule.state === 'hide' ? '✕' : '👁'}
        <Show when={props.rule.source !== 'default'}><small>{props.rule.source}</small></Show>
      </button>
    </Show>
  );
}

// HiddenList: in edit mode, list hidden money lines so the admin can unhide.
function HiddenList(props) {
  const hidden = () => {
    const shown = new Set([
      ...props.slip.earnings.map(l => l.code),
      ...props.slip.deductions.map(l => l.code),
    ]);
    return (props.components ?? []).filter(c =>
      c.kind !== 'info' && !shown.has(c.code) && props.ruleFor(c.code).state === 'hide');
  };
  return (
    <div class="sl-hidden-list">
      <For each={hidden()}>{(c) =>
        <button class="btn ghost" onClick={() => props.cycle(c.code)}>{c.code} ✕</button>
      }</For>
    </div>
  );
}
